import {
  addMilliseconds,
  differenceInMilliseconds,
  isValid,
  parse,
} from 'date-fns';
import { UnitsEnum } from '../../../../../services/entities/impl/blfApi/apiConstants';
import { ScanData } from '../../../../scanDataApi/scanDataDefinitions';

const DATE_PART_FMT = "yyyyMMdd'T'HHmmss";

/** Parses a “20250417T170912.810108”-style string to a Date with µs preserved */
function parseGpsTimestamp(ts: string) {
  const [whole, fractional = ''] = ts.split('.');

  // First parse the whole-seconds part
  const d = parse(whole, DATE_PART_FMT, new Date());
  if (!isValid(d)) return null;

  // Convert fractional seconds (up to 6 digits) → milliseconds + remaining µs
  const padded = (fractional + '000000').slice(0, 6); // exactly 6 digits
  const ms = Number(padded.slice(0, 3)); // first 3 → ms
  // const μs = Number(padded.slice(3)); // last 3 → μs (not used now)

  return addMilliseconds(d, ms);
}

/**
 * Calculates the average velocity between two points based on the total distance traveled and the time elapsed between the points.
 * The total distance input is already converted to the appropriate unit system (feet for Imperial, meters for Metric) before being passed to this function.
 * If either point is null or missing a timestamp, the function returns null.
 * @param point1 The first scan data point.
 * @param point2 The second scan data point.
 * @param totalDistance The total distance traveled between the two points, in the appropriate unit system (feet for Imperial, meters for Metric).
 * @param units The unit system to use for the calculation (Imperial or Metric).
 * @returns The average velocity between the two points in miles per hour or kilometers per hour, or null if calculation is not possible.
 */
export function calculateAverageVelocity(
  point1: ScanData | null,
  point2: ScanData | null,
  totalDistance: number,
  units: UnitsEnum
): number | null {
  // Check if either point is null or missing a timestamp
  if (
    !point1 ||
    !point2 ||
    !point1.point_timestamp ||
    !point2.point_timestamp
  ) {
    return null;
  }

  const t1 = parseGpsTimestamp(point1.point_timestamp);
  const t2 = parseGpsTimestamp(point2.point_timestamp);
  if (!t1 || !t2) return null;

  const ΔtSec = Math.abs(differenceInMilliseconds(t1, t2)) / 1000; // precise to 1 ms
  if (ΔtSec === 0) return Infinity;

  const distance =
    units === UnitsEnum.imperial
      ? totalDistance / 5_280 // ft → miles
      : totalDistance / 1_000; // m  → km

  return distance / (ΔtSec / 3_600); // hours in denominator
}
